/**
 * LEADS-LIST-SALESFORCE-VIEWS-V1 -- the Leads list VIEW SELECTOR (the
 * Salesforce list-view grammar): a prominent top-left dropdown that names
 * the ACTIVE view and opens the role's view set, replacing the former chip
 * row. Five-second test: the operator reads WHICH view they are in without
 * scanning a row of chips.
 *
 * PRESENTATION + COMPOSITION ONLY. The view set is the caller's role-shaped
 * list (viewsForRole -- composed over the EXISTING fenced reads); this
 * component never widens or narrows what a role may see. It carries the
 * copy/bookmark-URL door (the URL is the canonical view state, C-9) beside
 * the selector, exactly as the chip row did. Menu idiom mirrors the repo's
 * ColumnChooser / FilterBar token-menu (button + absolute menu +
 * outside-click + Escape). Selection + caret visuals are CSS-driven (no
 * glyphs -- authored lines stay ASCII-clean).
 */

import { useEffect, useRef, useState } from 'react';

export interface LeadViewOption {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
}

export interface LeadViewSelectorProps {
  readonly views: readonly LeadViewOption[];
  /** The resolved active view id (client-recency aware), or null = Custom. */
  readonly activeViewId: string | null;
  readonly onSelect: (id: string) => void;
  /** Copy the canonical URL of the current state (bookmarkable). */
  readonly onCopyUrl: () => void;
  /** True briefly after a successful copy (renders the confirmation). */
  readonly copied?: boolean;
}

export default function LeadViewSelector({
  views,
  activeViewId,
  onSelect,
  onCopyUrl,
  copied = false,
}: LeadViewSelectorProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const active = views.find((v) => v.id === activeViewId) ?? null;
  const triggerLabel = active !== null ? active.label : 'Custom view';

  return (
    <div className="lead-view-selector" data-testid="lead-view-selector">
      <div className="lead-view-selector__picker" ref={ref}>
        <span className="lead-view-selector__eyebrow">List view</span>
        <button
          type="button"
          className="lead-view-selector__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid="lead-view-selector-trigger"
          data-active-view={activeViewId ?? 'custom'}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <span className="lead-view-selector__trigger-label">{triggerLabel}</span>
          <span className="lead-view-selector__caret" aria-hidden="true" />
        </button>
        {open ? (
          <ul
            className="lead-view-selector__menu"
            role="listbox"
            aria-label="Choose a list view"
            data-testid="lead-view-selector-menu"
          >
            {views.map((v) => {
              const selected = v.id === activeViewId;
              return (
                <li key={v.id} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={
                      selected
                        ? 'lead-view-selector__option lead-view-selector__option--active'
                        : 'lead-view-selector__option'
                    }
                    data-testid={`lead-view-option-${v.id}`}
                    onClick={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                  >
                    <span className="lead-view-selector__option-text">
                      <span className="lead-view-selector__option-label">{v.label}</span>
                      {v.hint !== undefined ? (
                        <span className="lead-view-selector__option-hint">{v.hint}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <button
        type="button"
        className="lead-view-selector__copy"
        data-testid="lead-view-selector-copy"
        onClick={() => onCopyUrl()}
      >
        {copied ? 'Link copied' : 'Copy link to this view'}
      </button>
    </div>
  );
}
